import { Stack } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import TaskModalContent from '@components/Modal/Content/TaskModalContent';
import NullStateDiv from '@components/NullStateDiv';
import RoundedButton from '@components/RoundedButton/index';

import { useGetAllCuratorTasksForPatient } from '@hooks/api/curator-task';
import { useCuration } from '@hooks/useCuration';
import { useModal } from '@hooks/useModal';

import { sortDates } from '@utils/sort/date';

import TaskCard from '../TaskCard/TaskCard';
import { ButtonWrapper } from './CurationSIdeDrawer.style';

const messages = defineMessages({
  tasks: {
    id: 'curationSideDrawer.tasks',
    defaultMessage: "{name}'s Tasks",
  },
  noTasks: {
    id: 'curationSideDrawer.noTasks',
    defaultMessage: 'No Tasks',
  },
  addTask: {
    id: 'curationSideDrawer.addTask',
    defaultMessage: 'Add Task',
  },
});

export default function CurationSideDrawer({
  vitall_patient_id,
  name = '',
}: {
  vitall_patient_id: string | null;
  name?: string;
}) {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { vitall_patient },
  } = useCuration();
  const vitallPatientLoading = !vitall_patient;

  const { data: tasksData, loading: tasksLoading } =
    useGetAllCuratorTasksForPatient({ vitall_patient_id });

  const loading = tasksLoading || vitallPatientLoading;

  const tasks = (tasksData?.data ?? [])
    .slice()
    .filter(({ status }) => !status)
    .sort(({ due_date: dateA = null }, { due_date: dateB = null }) =>
      sortDates('desc')({ data: dateA }, { data: dateB })
    );

  const handleOpenTaskModal = () => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <TaskModalContent
          curator_id={vitall_patient?.curator_user_id}
          onSubmit={closeModal}
          profileView
        />
      ),
    });
  };

  return (
    <div>
      <ButtonWrapper>
        <RoundedButton
          autoSize
          fontSize={14}
          onClick={() => handleOpenTaskModal()}
          variant="red"
        >
          {formatMessage(messages.addTask)}
        </RoundedButton>
      </ButtonWrapper>
      <h3>
        {name
          ? formatMessage(messages.tasks, {
              name,
            })
          : ''}
      </h3>
      <ContentLoader loading={loading}>
        <Stack spacing={2}>
          {tasks.length ? (
            tasks.map((task) => (
              <TaskCard key={task.curator_task_id} task={task} />
            ))
          ) : (
            <NullStateDiv>{formatMessage(messages.noTasks)}</NullStateDiv>
          )}
        </Stack>
      </ContentLoader>
    </div>
  );
}
